import { of, merge, forkJoin, combineLatest, interval } from 'rxjs';
import {
  debounceTime,
  flatMap,
  map,
  switchMap,
  take,
  throttleTime,
  filter,
  tap,
  delay,
} from 'rxjs/operators';
import createRxStore, { actions, ofType, rxDispatch, skipUpdate } from '..';
import { DispatchType } from '../../types/rx-actions';
import {
  ConfigState,
  ConfigStatus,
  ConfigType,
  SensorConfigAction,
  SensorConfigActionTypes,
  WorkingConfig,
} from '../../types/sensor-configuration';
import { Capture, Sensor } from '../../types/api';
import {
  RESOURCE_IDLE,
  RESOURCE_LOADING,
  ResourceStatus,
} from '../../types/resource';
import { SORT_EMPTY } from '../../types/sort';
import { SenseiApi } from '../../utils/api';
import { getRequiredSessionToken } from '../../utils/session-token';
import { Filter, FilterOfValueType } from '../../types/filters';
import { updateQueryStringKey } from '../../utils/rx-store-serialization';
import { Toaster } from '@densityco/ui';

import { ConfigV1 } from '../../types/config';
import { fetchSelectedCaptureDetails } from '../../rx-actions/load-capture-details';

export const initialState: ConfigState = {
  config: RESOURCE_IDLE,
  selectedCapture: RESOURCE_IDLE,

  captures: {
    selectedId: null,
    searchText: '',
    filters: [],
    sort: SORT_EMPTY,
    resource: RESOURCE_IDLE,
  },

  reports: {
    capturesBeingRun: [],
    results: {},
  },
};

// A helper function used in the reducer below to make updating the
// working sensor configuration easier.
function updateWorkingConfig(
  state: ConfigState,
  configUpdater: (workingConfig: WorkingConfig) => WorkingConfig,
): ConfigState {
  if (state.config.status !== ResourceStatus.COMPLETE) {
    return state;
  }

  const workingConfig = configUpdater(state.config.data.workingConfig);

  // If the same config was returned that went into the config updater function, then
  // return the old state - nothing changed.
  if (workingConfig === state.config.data.workingConfig) {
    return state;
  }

  let sensorConfig: { type: ConfigType; config: ConfigV1 } | null = null;
  let workingConfigValidationError: Error | null = null;
  try {
    sensorConfig = WorkingConfig.toSensorFormat(workingConfig);
  } catch (e) {
    console.warn(`Error trying to parse working config: ${e}`);
    workingConfigValidationError = e;
  }

  return {
    ...state,
    config: {
      status: ResourceStatus.COMPLETE,
      data: {
        ...state.config.data,
        workingConfig: { ...workingConfig, clean: false },
        workingConfigValidationError,

        config_status: ConfigStatus.MODIFIED,

        mutable_config: sensorConfig
          ? sensorConfig.config
          : state.config.data.mutable_config,
        mutable_config_template: sensorConfig
          ? sensorConfig.type
          : state.config.data.mutable_config_template,
      },
    },
  };
}

function reducer(
  state: ConfigState,
  action: SensorConfigAction,
): ConfigState | typeof skipUpdate {
  switch (action.type) {
    case SensorConfigActionTypes.SENSOR_CONFIG_LOAD_ERROR:
      return {
        ...state,
        config: {
          status: ResourceStatus.ERROR,
          error: action.error,
        },
      };

    case SensorConfigActionTypes.SENSOR_CONFIG_LOAD_COMPLETE:
      return {
        ...state,
        config: {
          status: ResourceStatus.COMPLETE,
          data: {
            sensor: action.sensor,
            status,

            in_mpi: action.in_mpi,

            workingConfig: WorkingConfig.fromSensorFormat(
              action.mutable_config_template,
              action.mutable_config,
            ),
            workingConfigValidationError: null,
            mutable_config: action.mutable_config,
            mutable_config_template: action.mutable_config_template,
            assigned_config: action.assigned_config,
            assigned_config_template: action.assigned_config_template,
            config_status: action.config_status,

            initialAccuracyPercentage: action.initialAccuracyPercentage,
          },
        },
        captures: {
          ...state.captures,
          initialStartTimeSeconds: action.initialStartTimeSeconds,
        },
      };

    case SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_PUSH_NEW:
      if (state.captures.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }

      return {
        ...state,
        captures: {
          ...state.captures,
          resource: {
            status: ResourceStatus.COMPLETE,
            data: [
              ...action.captures.map((capture) => ({
                ...capture,
                highlighted: true,
              })),
              ...state.captures.resource.data,
            ],
          },
        },
      };

    case SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_PUSH_NEW:
      if (state.captures.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }

      return {
        ...state,
        captures: {
          ...state.captures,
          resource: {
            status: ResourceStatus.COMPLETE,
            data: state.captures.resource.data.map((capture) => ({
              ...capture,
              highlighted: false,
            })),
          },
        },
      };

    case SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_LOAD_START:
      return {
        ...state,
        captures: {
          ...state.captures,
          resource: RESOURCE_LOADING,
        },
      };

    case SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_LOAD_ERROR:
      return {
        ...state,
        captures: {
          ...state.captures,
          resource: { status: ResourceStatus.ERROR, error: action.error },
        },
      };

    case SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_LOAD_COMPLETE:
      let selectedId;
      if (action.initialSelectedCaptureId) {
        selectedId = action.initialSelectedCaptureId;
      } else {
        // fall back to the first capture in the response
        selectedId = action.captures.length > 0 ? action.captures[0].id : null;
      }
      return {
        ...state,
        captures: {
          ...state.captures,
          selectedId,
          resource: {
            status: ResourceStatus.COMPLETE,
            data: action.captures.map((capture) => ({
              ...capture,
              highlighted: false,
            })),
          },
        },
      };

    case SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_SET_FILTERS:
      return {
        ...state,
        captures: {
          ...state.captures,
          filters: action.filters,
        },
      };

    case SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_SET_SEARCH:
      return {
        ...state,
        captures: {
          ...state.captures,
          searchText: action.search,
        },
      };

    case SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_SET_SORT:
      return {
        ...state,
        captures: {
          ...state.captures,
          sort: { column: action.column, direction: action.direction },
          resource: {
            status: ResourceStatus.COMPLETE,
            data: action.captures,
          },
        },
      };

    case SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_SET_SELECTED:
      return {
        ...state,
        captures: {
          ...state.captures,
          selectedId: action.selectedId,
          initialStartTimeSeconds: action.startTime,
        },
      };
  }

  if (state.config.status !== ResourceStatus.COMPLETE) {
    return state;
  }

  switch (action.type) {
    case SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TYPE:
      return updateWorkingConfig(state, (workingConfig) => {
        return WorkingConfig.migrateConfigType(
          workingConfig,
          action.configType,
        );
      });

    case SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_MAX_PIXEL_INTENSITY:
      return updateWorkingConfig(state, (workingConfig) => {
        if (workingConfig.type === ConfigType.RAW) {
          return workingConfig;
        }
        return {
          ...workingConfig,
          maxPixelIntensity: action.maxPixelIntensity,
        };
      });

    // This action is fired when changing the trigger line graphically in the capture viewer
    case SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE:
      return updateWorkingConfig(state, (workingConfig) => {
        if (
          workingConfig.type !== ConfigType.TRIGGER_LINE_TOP &&
          workingConfig.type !== ConfigType.TRIGGER_LINE_BOTTOM
        ) {
          return workingConfig;
        }
        return {
          ...workingConfig,
          triggerLineX0: `${action.x0}`,
          triggerLineX1: `${action.x1}`,
          triggerLineY0: `${action.y0}`,
          triggerLineY1: `${action.y1}`,
        };
      });

    // These next 4 actions are fired when changing the trigger line using the text boxes in the form.
    case SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_X0:
      return updateWorkingConfig(state, (workingConfig) => {
        if (
          workingConfig.type !== ConfigType.TRIGGER_LINE_TOP &&
          workingConfig.type !== ConfigType.TRIGGER_LINE_BOTTOM
        ) {
          return workingConfig;
        }
        return {
          ...workingConfig,
          triggerLineX0: action.x0,
        };
      });
    case SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_X1:
      return updateWorkingConfig(state, (workingConfig) => {
        if (
          workingConfig.type !== ConfigType.TRIGGER_LINE_TOP &&
          workingConfig.type !== ConfigType.TRIGGER_LINE_BOTTOM
        ) {
          return workingConfig;
        }
        return {
          ...workingConfig,
          triggerLineX1: action.x1,
        };
      });
    case SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_Y0:
      return updateWorkingConfig(state, (workingConfig) => {
        if (
          workingConfig.type !== ConfigType.TRIGGER_LINE_TOP &&
          workingConfig.type !== ConfigType.TRIGGER_LINE_BOTTOM
        ) {
          return workingConfig;
        }
        return {
          ...workingConfig,
          triggerLineY0: action.y0,
        };
      });
    case SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_Y1:
      return updateWorkingConfig(state, (workingConfig) => {
        if (
          workingConfig.type !== ConfigType.TRIGGER_LINE_TOP &&
          workingConfig.type !== ConfigType.TRIGGER_LINE_BOTTOM
        ) {
          return workingConfig;
        }
        return {
          ...workingConfig,
          triggerLineY1: action.y1,
        };
      });

    case SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_UPPER_BOUND:
      return updateWorkingConfig(state, (workingConfig) => {
        if (
          workingConfig.type !== ConfigType.TRIGGER_LINE_TOP &&
          workingConfig.type !== ConfigType.TRIGGER_LINE_BOTTOM
        ) {
          return workingConfig;
        }

        return {
          ...workingConfig,
          upperBound: action.upperBound,
          lowerBound: workingConfig.boundsLinked
            ? action.upperBound
            : workingConfig.lowerBound,
        };
      });
    case SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_LOWER_BOUND:
      return updateWorkingConfig(state, (workingConfig) => {
        if (
          workingConfig.type !== ConfigType.TRIGGER_LINE_TOP &&
          workingConfig.type !== ConfigType.TRIGGER_LINE_BOTTOM
        ) {
          return workingConfig;
        }
        return {
          ...workingConfig,
          lowerBound: action.lowerBound,
          upperBound: workingConfig.boundsLinked
            ? action.lowerBound
            : workingConfig.upperBound,
        };
      });
    case SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_BOUNDS_LINKED:
      return updateWorkingConfig(state, (workingConfig) => {
        if (
          workingConfig.type !== ConfigType.TRIGGER_LINE_TOP &&
          workingConfig.type !== ConfigType.TRIGGER_LINE_BOTTOM
        ) {
          return workingConfig;
        }

        let newWorkingConfig = {
          ...workingConfig,
          boundsLinked: action.linked,
        };

        // Ensure that when the bounds are linked, the upper and lower bounds are set equal to each
        // other.
        if (newWorkingConfig.boundsLinked) {
          newWorkingConfig.lowerBound = newWorkingConfig.upperBound;
        }

        return newWorkingConfig;
      });
    case SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_SINGULARITY_REGION:
      if (
        state.config.data.workingConfig.type !==
          ConfigType.SINGULARITY_BOTTOM &&
        state.config.data.workingConfig.type !== ConfigType.SINGULARITY_TOP &&
        state.config.data.workingConfig.type !==
          ConfigType.SINGULARITY_WALL_RIGHT &&
        state.config.data.workingConfig.type !==
          ConfigType.SINGULARITY_WALL_LEFT &&
        state.config.data.workingConfig.type !==
          ConfigType.SINGULARITY_POLYGONAL
      ) {
        return state;
      }
      return updateWorkingConfig(state, (workingConfig) => ({
        ...workingConfig,
        region: action.region,
        senseiExpression: action.senseiExpression,
      }));
    case SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_MINIMUM_PATH_LENGTH:
      if (
        state.config.data.workingConfig.type !==
          ConfigType.SINGULARITY_BOTTOM &&
        state.config.data.workingConfig.type !== ConfigType.SINGULARITY_TOP &&
        state.config.data.workingConfig.type !==
          ConfigType.SINGULARITY_WALL_RIGHT &&
        state.config.data.workingConfig.type !==
          ConfigType.SINGULARITY_WALL_LEFT &&
        state.config.data.workingConfig.type !==
          ConfigType.SINGULARITY_POLYGONAL
      ) {
        return state;
      }
      return updateWorkingConfig(state, (workingConfig) => ({
        ...workingConfig,
        minimumPathLength: action.minimumPathLength,
      }));

    case SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_ALGORITHM:
      if (state.config.data.workingConfig.type === ConfigType.RAW) {
        return state;
      }
      return updateWorkingConfig(state, (workingConfig) => ({
        ...workingConfig,
        algorithm: action.algorithm,
      }));

    case SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_RAW_CONFIG:
      if (state.config.data.workingConfig.type !== ConfigType.RAW) {
        return state;
      }
      return updateWorkingConfig(state, (workingConfig) => ({
        ...workingConfig,
        data: action.rawConfig,
      }));

    case SensorConfigActionTypes.SENSOR_CONFIG_RESET_CONFIGURATION:
      return {
        ...state,
        config: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.config.data,
            workingConfig: WorkingConfig.fromSensorFormat(
              state.config.data.assigned_config_template,
              state.config.data.assigned_config,
            ),
            mutable_config: state.config.data.assigned_config,
            mutable_config_template: state.config.data.assigned_config_template,
            config_status: ConfigStatus.EXISTING,
          },
        },
      };

    case SensorConfigActionTypes.SENSOR_CONFIG_ADD_REPORT_LOADING:
      return {
        ...state,
        reports: {
          ...state.reports,

          capturesBeingRun: state.reports.capturesBeingRun.concat(
            action.capture_id,
          ),
        },
      };

    case SensorConfigActionTypes.SENSOR_CONFIG_SET_REPORT_COMPLETE:
      return {
        ...state,
        reports: {
          ...state.reports,

          capturesBeingRun: state.reports.capturesBeingRun.filter(
            (id) => id !== action.capture_id,
          ),
          results: {
            ...state.reports.results,
            [action.capture_id]: { totalAccuracy: action.accuracy },
          },
        },
      };

    case SensorConfigActionTypes.SENSOR_CONFIG_RESET_REPORT:
      return {
        ...state,
        reports: {
          capturesBeingRun: [],
          results: {},
        },
      };

    case SensorConfigActionTypes.SENSOR_CONFIG_SELECTED_CAPTURE_LOAD_COMPLETE:
      return {
        ...state,
        selectedCapture: {
          status: ResourceStatus.COMPLETE,
          data: action.selectedCapture,
        },
      };

    default:
      return skipUpdate;
  }
}

const ConfigStore = createRxStore('ConfigStore', initialState, reducer);

// Fetch a list of all captures for a sensor
function generateCaptureQueryParams(filters: Array<Filter>, search: string) {
  const status = filters.filter((f) => f.definitionId === 'status');
  const tags = filters.filter((f) => f.definitionId === 'tags');
  const accuracyRangeLessThan = filters.find(
    (i) => i.definitionId === 'total_accuracy' && i.operatorId === 'lt',
  ) as FilterOfValueType<string> | undefined;

  const accuracyRangeGreaterThan = filters.find(
    (i) => i.definitionId === 'total_accuracy' && i.operatorId === 'gt',
  ) as FilterOfValueType<string> | undefined;

  let params = {
    annotation_status:
      status.length > 0 ? status.map((i) => i.value).join(',') : undefined,
    tag_ids: tags.length > 0 ? tags.map((t) => t.value).join(',') : undefined,
    accuracy_less_than: accuracyRangeLessThan
      ? accuracyRangeLessThan.value
      : undefined,
    accuracy_greater_than: accuracyRangeGreaterThan
      ? accuracyRangeGreaterThan.value
      : undefined,
  };
  if (search) {
    params['search'] = search;
  }
  return params;
}
async function fetchCapturesForSensor(
  dispatch: DispatchType,
  jwtToken: string,
  sensor: Sensor,
  filters: Array<Filter>,
  search: string,
  initialSelectedCaptureId?: Capture['id'],
  isInitialLoad = true,
) {
  if (isInitialLoad) {
    dispatch({
      type: SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_LOAD_START,
    });
  }

  const params = generateCaptureQueryParams(filters, search);

  let captures;
  try {
    captures = await SenseiApi.sensorCaptureList(
      jwtToken,
      sensor.serial_number,
      params,
    );
  } catch (error) {
    dispatch({
      type: SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_LOAD_ERROR,
      error,
    });
    return;
  }

  dispatch({
    type: SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_LOAD_COMPLETE,
    captures: captures.results,
    sensorSerialNumber: sensor.serial_number,
    initialSelectedCaptureId,
  });
}

// =================================
// SIDE EFFECT: When sensor configuration page is loaded, load sensor and captures.
//              Also, kick off polling the captures endpoint in the background to auto-load
//              new captures as they are received from units.
// =================================
const enterPageStream = actions.pipe(
  ofType<{ sensorSerialNumber: Sensor['serial_number'] }>(
    SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_PAGE,
  ),
);

// Initial data loading
enterPageStream
  .pipe(
    switchMap(
      () => ConfigStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(async ({ action, state }) => {
    const jwtToken = getRequiredSessionToken();
    const sensorSerialNumber = action.sensorSerialNumber;

    const initialStartTimeSeconds = (action as any).params['t'];

    rxDispatch({ type: SensorConfigActionTypes.SENSOR_CONFIG_LOAD_LOADING });
    rxDispatch({
      type: SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_LOAD_START,
    });

    let sensor;
    try {
      sensor = await SenseiApi.sensorDetails(jwtToken, sensorSerialNumber);
    } catch (error) {
      rxDispatch({
        type: SensorConfigActionTypes.SENSOR_CONFIG_LOAD_ERROR,
        error,
      });
      return;
    }

    rxDispatch({
      type: SensorConfigActionTypes.SENSOR_CONFIG_LOAD_COMPLETE,
      sensor,

      in_mpi: sensor.in_mpi,

      assigned_config: sensor.assigned_config,
      assigned_config_template: ConfigType[sensor.assigned_config_template],

      mutable_config: sensor.assigned_config,
      mutable_config_template: ConfigType[sensor.assigned_config_template],

      config_status: ConfigStatus[sensor.config_status],

      initialAccuracyPercentage: sensor.latest_report
        ? sensor.latest_report.total_accuracy
        : null,

      initialStartTimeSeconds,
    });

    await fetchCapturesForSensor(
      rxDispatch,
      jwtToken,
      sensor,
      state.captures.filters,
      state.captures.searchText,
    );
  });

// Captures endpoint polling
const CAPTURES_POLL_INTERVAL_SECONDS = 10;
const leavePageStream = actions.pipe(
  ofType(SensorConfigActionTypes.SENSOR_CONFIG_LEAVE_PAGE),
);
const newCapturesStream = merge(enterPageStream, leavePageStream).pipe(
  switchMap((action) => {
    if (action.type === SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_PAGE) {
      return interval(CAPTURES_POLL_INTERVAL_SECONDS * 1000).pipe(
        // Every interval "tick", return the initial CHANGE_PAGE action and a snapshot of the state in the store
        flatMap((pollNumber) =>
          forkJoin(of(action), ConfigStore.pipe(take(1))),
        ),
      );
    } else {
      return of();
    }
  }),
  filter(([_, state]) => state.config.status === ResourceStatus.COMPLETE),
  filter(
    ([_, state]) => state.captures.resource.status === ResourceStatus.COMPLETE,
  ),
  flatMap(([action, state]) => {
    const jwtToken = getRequiredSessionToken();
    const sensorSerialNumber = action.sensorSerialNumber;

    const filters = state.captures.filters;
    const search = state.captures.searchText;

    const params = generateCaptureQueryParams(filters, search);

    return combineLatest(
      of(state),
      SenseiApi.sensorCaptureList(jwtToken, sensorSerialNumber, params).catch(
        (error) =>
          Toaster.showToast({ text: 'Error polling captures!', type: 'error' }),
      ),
    );
  }),

  // Ensure a response came back from the polling attempt
  filter(([_, capturesResponse]) => Boolean(capturesResponse)),

  map(([state, capturesResponse]: [ConfigState, any]) => {
    if (state.captures.resource.status !== ResourceStatus.COMPLETE) {
      // This should be an impossible case, because of the filter above, but typescript needs this
      return;
    }
    const captureList = state.captures.resource.data;

    // Get all captures that are new, and add them to the start of the captures list
    const existingCaptureIds = new Set(captureList.map((c) => c.id));
    return capturesResponse.results.filter(
      (capture) => !existingCaptureIds.has(capture.id),
    ) as Array<Capture>;
  }),
  filter((newCaptures) => newCaptures.length > 0),
);

newCapturesStream.subscribe((newCaptures) => {
  rxDispatch({
    type: SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_PUSH_NEW,
    captures: newCaptures,
  });
});
// After a certain window, remove the highlight values just so that the data makes more sense.
//
// The ListView component manages all its own animation (in css) )so
// this doesn't do anything to trigger the fade out of the highlight
// directly.
newCapturesStream.pipe(delay(3500)).subscribe((newCaptures) => {
  rxDispatch({
    type: SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_CLEAR_HIGHLIGHT,
  });
});

// =================================
// SIDE EFFECT: when changing the filters for the captures section, reload the data with the applied filters.
// =================================
actions
  .pipe(
    ofType(SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_SET_FILTERS),
    switchMap(
      () => ConfigStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();
    if (state.config.status !== ResourceStatus.COMPLETE) {
      return;
    }

    fetchCapturesForSensor(
      rxDispatch,
      jwtToken,
      state.config.data.sensor,
      state.captures.filters,
      state.captures.searchText,
    );
  });

// =================================
// SIDE EFFECT: When search text changes, throttle the events but refetch the results
// from the server at most 1 second after typing has completed.
// =================================
actions
  .pipe(
    ofType(SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_SET_SEARCH),
    debounceTime(1000),
    switchMap(
      () => ConfigStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();
    if (state.config.status !== ResourceStatus.COMPLETE) {
      return;
    }

    fetchCapturesForSensor(
      rxDispatch,
      jwtToken,
      state.config.data.sensor,
      state.captures.filters,
      state.captures.searchText,
    );
  });

// =================================
// SIDE EFFECT: when changing the selected capture, update the querystring to include it
// and start load of capture details (events, annotations)
// =================================
merge(
  actions.pipe(
    ofType(SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_LOAD_COMPLETE),
    switchMap(
      () => ConfigStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  ),
  actions.pipe(
    ofType(SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_SET_SELECTED),
    switchMap(
      () => ConfigStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  ),
).subscribe(({ action, state }) => {
  if (!state.captures.selectedId) {
    return;
  }

  updateQueryStringKey('capture_id', state.captures.selectedId);

  const jwtToken = getRequiredSessionToken();
  fetchSelectedCaptureDetails(rxDispatch, jwtToken, state.captures.selectedId);
});

// =================================
// SIDE EFFECT: when changing the seek time in the capture viewer, update the querystring with the correct `t`
// querystring parameter.
// =================================
actions
  .pipe(
    ofType(SensorConfigActionTypes.SENSOR_CONFIG_SELECTED_CAPTURE_VIEWER_SEEK),
    throttleTime(66),
  )
  .subscribe((action) => {
    updateQueryStringKey('t', (action as any).seek);
  });

export default ConfigStore;
